import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { sizes, colours } from '@qflow/theme'
import Draggable from 'react-draggable'
import type { DraggableData, DraggableEvent } from 'react-draggable'
import * as AntIcons from '@ant-design/icons'
import {
  Modal as AntModal,
  Button,
  Col,
  Radio,
  RadioChangeEvent,
  Row,
  Space,
  Spin,
  Tooltip,
  Input
} from 'antd'
import { icons, Text, Spacer, ColourButton } from 'portal/lib/primitives'
import { useModuleIds } from 'portal/lib/hooks'
import {
  sugstAddrsSearch,
  addrsSearchStart
} from 'portal/store/address/actions'
import {
  KnownAddressResult,
  ClientAddressName
} from './ClientKnownAddressModalLink'

type SuggestedAddress = {
  fullAddress: string
  coordinates: {
    lat: number
    lon: number
  }
  postCode: string | null
  country: string | null
  count: number | null
  score: number | null
}

export type ClientKnownAddressModalProps = {
  addressName: ClientAddressName
  isOpen: boolean
  onCancel: () => void
  onSubmit: (result: KnownAddressResult) => void
}

export function ClientKnownAddressModal({
  addressName,
  isOpen,
  onCancel,
  onSubmit
}: ClientKnownAddressModalProps) {
  const [bounds, setBounds] = useState({ left: 0, top: 0, bottom: 0, right: 0 })
  const draggleRef = useRef<HTMLDivElement>(null)

  const onStart = (_event: DraggableEvent, uiData: DraggableData) => {
    const { clientWidth, clientHeight } = window.document.documentElement
    const targetRect = draggleRef.current?.getBoundingClientRect()
    if (!targetRect) {
      return
    }
    setBounds({
      left: -targetRect.left + uiData.x,
      right: clientWidth - (targetRect.right - uiData.x),
      top: -targetRect.top + uiData.y,
      bottom: clientHeight - (targetRect.bottom - uiData.y)
    })
  }

  const modalTitle = useMemo(() => `Edit ${addressName} Address`, [addressName])

  const renderPanel = useCallback(() => {
    return (
      <SuggestionAddressPanel
        addressName={addressName}
        onCancel={onCancel}
        onSubmit={onSubmit}
      />
    )
  }, [addressName, onCancel, onSubmit])

  return (
    <AntModal
      title={
        <div
          style={{
            width: '100%',
            cursor: 'move'
          }}
        >
          <Text dim>{modalTitle}</Text>
        </div>
      }
      open={isOpen}
      closeIcon={<icons.Cross colour={colours.BLACK} size={sizes.SMALL} />}
      onCancel={onCancel}
      maskClosable={false}
      footer={null}
      width={600}
      wrapClassName="address-modal"
      modalRender={modal => (
        <Draggable
          bounds={bounds}
          onStart={(event, uiData) => onStart(event, uiData)}
        >
          <div ref={draggleRef}>{modal}</div>
        </Draggable>
      )}
      transitionName=""
    >
      {renderPanel()}
    </AntModal>
  )
}

function SuggestionAddressPanel({
  addressName,
  onCancel,
  onSubmit
}: {
  addressName: ClientAddressName
  onCancel: () => void
  onSubmit: (result: KnownAddressResult) => void
}) {
  const [enteredValue, setEnteredValue] = useState<string>('')
  const [selectedAddress, setSelectedAddress] =
    useState<SuggestedAddress | null>()
  const { accountId, projectId, moduleName, recordId } = useModuleIds()

  const dispatch = useDispatch()

  function toAddressType(value: ClientAddressName) {
    switch (value) {
      case 'Delivery':
      case 'Site':
        return 'deliveryAddress'
      case 'Dispatch':
        return 'dispatchAddress'
      case 'Origin Site':
        return 'originSiteAddress'
      case 'Facility Site':
        return 'facilitySiteAddress'
    }
  }

  const {
    isLoadingSuggestAddresses,
    suggestAddressesError,
    suggestionsGeneral,
    isLoadingAddressSearch,
    addressSearchError,
    foundAddresses,
    addressesHasSearched,
    siteAddress
  } = useSelector((state: any) => state.address)

  const isLoading = isLoadingSuggestAddresses || isLoadingAddressSearch

  useEffect(() => {
    dispatch(
      sugstAddrsSearch(
        accountId,
        projectId,
        moduleName,
        recordId,
        toAddressType(addressName)
      )
    )
  }, [accountId, addressName, dispatch, moduleName, projectId, recordId])

  function handleNewAddressSearching() {
    if (enteredValue == '') {
      return
    }
    dispatch(addrsSearchStart(accountId, projectId, enteredValue))
  }

  const handleSuggestedResultSelected = useCallback(
    (e: RadioChangeEvent) => {
      const [selectedType, index] = e.target.value.split('|')
      const selectedAddress =
        selectedType == 'SiteAddress' ? siteAddress : suggestionsGeneral[index]
      setSelectedAddress(selectedAddress)
    },
    [siteAddress, suggestionsGeneral]
  )

  const handleMatchedResultSelected = useCallback(
    e => {
      const selectedMatchedAddress = foundAddresses[e.target.value]
      setSelectedAddress(selectedMatchedAddress)
    },
    [foundAddresses]
  )

  const handleSubmit = useCallback(() => {
    if (!selectedAddress) {
      console.warn('Submit unexpectedly had no selected address')
      return
    }
    onSubmit({
      entered: enteredValue ?? '',
      known: selectedAddress.fullAddress,
      coordinates: selectedAddress.coordinates
    })
  }, [enteredValue, onSubmit, selectedAddress])

  const renderSuggestedAddresses = useCallback(
    (resultType: 'SiteAddress' | 'General', addresses: SuggestedAddress[]) => (
      <Space direction="vertical">
        {addresses.map((a, i) => (
          <Radio key={i} value={`${resultType}|${i}`}>
            <span className="emphasise">{a.fullAddress}</span>
          </Radio>
        ))}
      </Space>
    ),
    []
  )

  const SearchButton = (
    <Button
      tabIndex={-1}
      icon={<AntIcons.SearchOutlined />}
      onClick={() => handleNewAddressSearching()}
      aria-label="Search"
      disabled={!enteredValue}
      style={{
        borderTopRightRadius: 5,
        borderBottomRightRadius: 5,
        borderColor: colours.BORDER_GREY,
        backgroundColor: enteredValue ? colours.TEAL : colours.CLOUD_GREY,
        color: colours.WHITE
      }}
    />
  )

  return (
    <>
      {(suggestionsGeneral.length != 0 || siteAddress != null) && (
        <h2>Select an existing address</h2>
      )}
      {suggestionsGeneral.length == 0 && siteAddress == null && (
        <h2>Search street address</h2>
      )}
      {isLoading && (
        <Row>
          <Col offset={10} span={4}>
            <Spin tip="Finding Suggestions..." />
          </Col>
        </Row>
      )}

      {!isLoading && suggestAddressesError && (
        <p className="error">
          Failed to retrieve suggested addresses: {suggestAddressesError}
        </p>
      )}

      {!isLoading &&
        !suggestAddressesError &&
        (suggestionsGeneral.length != 0 || siteAddress != null) && (
          <Radio.Group onChange={handleSuggestedResultSelected}>
            {siteAddress && (
              <Col>
                <Row>
                  <Text size={sizes.SMALL_MEDIUM} dim>
                    Default project site address
                  </Text>
                </Row>
                <Spacer.Fine />
                <Row>
                  {renderSuggestedAddresses('SiteAddress', [siteAddress])}
                </Row>
              </Col>
            )}
            {suggestionsGeneral.length > 0 && (
              <Col>
                <Spacer.Fine />
                <Row>
                  <Text size={sizes.SMALL_MEDIUM} dim>
                    {addressName} addresses on other records
                  </Text>
                </Row>
                <Spacer.Fine />
                <Row>
                  {renderSuggestedAddresses('General', suggestionsGeneral)}
                </Row>
              </Col>
            )}
          </Radio.Group>
        )}
      {(suggestionsGeneral.length != 0 || siteAddress != null) && (
        <>
          <Spacer.Large />
          <h2>or, search street address</h2>
        </>
      )}

      <Row style={{ marginBottom: '10px' }}>
        <Col flex="auto">
          <Input.Search
            id="addressSearchInput"
            defaultValue={''}
            onChange={e => setEnteredValue(e.target.value)}
            onPressEnter={() => handleNewAddressSearching()}
            enterButton={SearchButton}
          />
        </Col>
      </Row>

      {isLoading && (
        <Row>
          <Col offset={10} span={4}>
            <Spin tip="Searching..." />
          </Col>
        </Row>
      )}

      {!isLoading && addressSearchError && (
        <p className="error">
          Failed to search addresses: {addressSearchError}
        </p>
      )}

      {!isLoading &&
        !addressSearchError &&
        addressesHasSearched &&
        foundAddresses.length === 0 && (
          <p className="warning">No addresses found matching search text</p>
        )}

      {!isLoading && !addressSearchError && foundAddresses.length > 0 && (
        <Radio.Group onChange={handleMatchedResultSelected}>
          <Space direction="vertical">
            {foundAddresses.map((a, i) => (
              <Radio key={i} value={i}>
                <Tooltip title={`${a.country}`}>
                  <span className="emphasise">{a.fullAddress}</span>
                </Tooltip>
              </Radio>
            ))}
          </Space>
        </Radio.Group>
      )}

      <Row style={{ justifyContent: 'flex-end', marginTop: '1.5rem' }}>
        <Col>
          <ColourButton.Basic onClick={onCancel}>Cancel</ColourButton.Basic>
        </Col>
        <Spacer.Tiny />
        <Col>
          <ColourButton.Action
            onClick={handleSubmit}
            disabled={!selectedAddress}
          >
            Set and close
          </ColourButton.Action>
        </Col>
      </Row>
    </>
  )
}
